import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contentId", "lessonId", "language", "trackId"],
  _excluded2 = ["shortId"],
  _excluded3 = ["shortId"];
import Raven from 'raven-js';
import I18n from 'I18n';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { na1 } from 'hubspot-url-utils/hublets';
import { Image } from 'foundations-components/transitional/components/Image';
import { Metrics } from '../../../Metrics';
import { useVideo } from '../videoQueries';
import { useVideoPlayerConfig } from '../videoHooks';
import { useShortVideo } from '../../shortContent/shortContentHooks';
import ACVideoPlayer from 'academy-common-lib/components/video/ACVideoPlayer';
import UsageTracker from '../../../utils/UsageTracker';
import { usePlaylistIdFromSearch } from '../../playlistContent/hooks';
import { usePlaylist } from '../../savedItems/savedItemsQueries';
import { useIsHubspotPlaylist } from '../../savedItems/savedItemsHooks';
import { jsx as _jsx } from "react/jsx-runtime";
const EmbeddedVideoBase = ({
  autoPlay,
  onFinish,
  onPlay,
  onProgress,
  onActivation,
  onLoaded,
  onReady,
  onTimeUpdate,
  mutedByDefault,
  playSpeed,
  fallbackThumbnailUrl,
  videoId,
  creatorHubId,
  creatorRegion,
  contentId,
  startTime,
  language,
  subtitleLanguage,
  renderContext = 'embedded-academy',
  videoType,
  lessonId,
  shortId,
  trackId
}) => {
  const playlistId = usePlaylistIdFromSearch();
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlist);
  const playbackRate = playSpeed && playSpeed / 100;
  const {
    setSubtitleLanguage,
    setPlaySpeed
  } = useVideoPlayerConfig();
  const previousSelectedLanguage = useRef(subtitleLanguage);
  // Cache the video player instance for seeking operations
  const currentEmbedPlayer = useRef(null);

  // Find and cache the video player instance by videoId from the global hsVideoApi
  const resolvePlayer = useCallback(() => {
    var _window$hsVideoApi;
    if (currentEmbedPlayer.current) return;
    const hsEmbedPlayers = ((_window$hsVideoApi = window.hsVideoApi) === null || _window$hsVideoApi === void 0 ? void 0 : _window$hsVideoApi.getPlayers()) || [];
    currentEmbedPlayer.current = hsEmbedPlayers.find(p => {
      var _p$options, _p$options$toString, _videoId$toString;
      return (p === null || p === void 0 || (_p$options = p.options) === null || _p$options === void 0 || (_p$options = _p$options.id) === null || _p$options === void 0 || (_p$options$toString = _p$options.toString) === null || _p$options$toString === void 0 ? void 0 : _p$options$toString.call(_p$options)) === (videoId === null || videoId === void 0 || (_videoId$toString = videoId.toString) === null || _videoId$toString === void 0 ? void 0 : _videoId$toString.call(videoId));
    }) || null;
  }, [videoId]);
  const handleOnProgress = useCallback(({
    currentTime,
    subtitleLanguage: _language
  }) => {
    var _currentEmbedPlayer$c;
    previousSelectedLanguage.current = subtitleLanguage;
    const hasSubtitle = !!((_currentEmbedPlayer$c = currentEmbedPlayer.current) !== null && _currentEmbedPlayer$c !== void 0 && (_currentEmbedPlayer$c = _currentEmbedPlayer$c.video) !== null && _currentEmbedPlayer$c !== void 0 && (_currentEmbedPlayer$c = _currentEmbedPlayer$c.subtitleLanguageCodes) !== null && _currentEmbedPlayer$c !== void 0 && _currentEmbedPlayer$c.length);
    if (onProgress) {
      onProgress({
        currentTime,
        subtitleLanguage: _language
      });
    }
    if (previousSelectedLanguage.current !== _language && hasSubtitle) {
      setSubtitleLanguage(_language);
    }
  }, [onProgress, setSubtitleLanguage, subtitleLanguage]);

  // if the video is played from a playlist, we need to track the playlist info
  const playlistInfo = useMemo(() => {
    return Object.assign({}, playlistId && {
      playlistId
    }, playlist && {
      isHubspotPlaylist
    }, playlist && {
      playlistName: playlist.name
    });
  }, [playlistId, playlist, isHubspotPlaylist]);
  const onVideoLoaded = useCallback(() => {
    Metrics.counter('hs-video-rendered-successfully').increment();
    UsageTracker.track('videoUsage', Object.assign({
      type: 'loaded',
      playerType: 'HUBSPOT_VIDEO',
      creatorHubId,
      videoId,
      contentId,
      renderContext,
      videoType,
      lessonId,
      shortId
    }, playlistInfo));
    // Ensure player ref is set when video finishes loading (fallback to onReady)
    resolvePlayer();
    if (onLoaded) onLoaded();
  }, [creatorHubId, videoId, contentId, playlistInfo, onLoaded, renderContext, videoType, lessonId, shortId, resolvePlayer]);
  const handlePlay = useCallback(() => {
    UsageTracker.track('videoUsage', Object.assign({
      type: 'started',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      autoPlay,
      creatorHubId,
      contentId,
      renderContext,
      videoType,
      lessonId,
      shortId
    }, playlistInfo));
    Metrics.counter('hs-video-play-clicked').increment();
    if (onPlay) onPlay();
  }, [videoId, autoPlay, creatorHubId, contentId, playlistInfo, onPlay, renderContext, videoType, lessonId, shortId]);
  const handleFinish = useCallback(() => {
    UsageTracker.track('videoUsage', Object.assign({
      type: 'finished',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      creatorHubId,
      contentId,
      renderContext,
      videoType,
      lessonId,
      shortId
    }, playlistInfo));
    if (onFinish) onFinish();
  }, [videoId, creatorHubId, contentId, playlistInfo, onFinish, renderContext, videoType, lessonId, shortId]);
  const handleActivation = useCallback(() => {
    UsageTracker.track('videoUsage', Object.assign({
      type: 'engaged',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      creatorHubId,
      contentId,
      renderContext,
      videoType,
      lessonId,
      shortId
    }, playlistInfo));
    if (onActivation) onActivation();
  }, [videoId, creatorHubId, contentId, playlistInfo, onActivation, videoType, renderContext, lessonId, shortId]);
  const handleMinutesWatched = useCallback(viewDuration => {
    UsageTracker.track('videoUsage', Object.assign({
      type: 'minutes-watched',
      playerType: 'HUBSPOT_VIDEO',
      videoId,
      viewDuration,
      creatorHubId,
      contentId,
      renderContext,
      videoType,
      lessonId,
      shortId
    }, playlistInfo));
  }, [videoId, creatorHubId, contentId, playlistInfo, renderContext, videoType, lessonId, shortId]);
  const hanldeOnPlayBackRateChange = useCallback(e => {
    const videoSpeed = e.detail.playbackRate;
    setPlaySpeed(videoSpeed * 100);
  }, [setPlaySpeed]);
  const handleReady = () => {
    resolvePlayer();
    if (onReady) onReady();
  };
  const hsVideoOnError = useCallback(e => {
    Metrics.counter('hs-video-failed-to-render').increment();
    Raven.captureException(new Error('HS_VIDEO_FAILED_RENDER'), {
      extra: {
        videoId,
        creatorHubId,
        errorType: e.detail.errorType,
        errorMessage: e.detail.payload || e.detail.message
      }
    });
  }, [creatorHubId, videoId]);
  useEffect(() => {
    Metrics.counter('hs-video-component-render').increment();
    UsageTracker.track('videoUsage', Object.assign({
      type: 'rendered',
      playerType: 'HUBSPOT_VIDEO',
      creatorHubId,
      videoId,
      contentId,
      renderContext,
      videoType,
      lessonId,
      shortId
    }, playlistInfo));
  }, [contentId, creatorHubId, videoId, renderContext, playlistInfo, videoType, lessonId, shortId]);
  const captionLanguage = mutedByDefault && language ? subtitleLanguage || language.toLowerCase() : subtitleLanguage;
  const getSavedStartTime = useCallback(() => {
    // Only honor explicit startTime if it is a positive number.
    // A value of 0 should not override a saved resume time.
    if (startTime && startTime > 0) return startTime;

    // Use ternary operators to determine id and type more safely
    const {
      id,
      type
    } = trackId ? {
      id: Number(trackId) || 0,
      type: 'TRACK'
    } : shortId ? {
      id: shortId,
      type: 'SHORT_FORM_CONTENT'
    } : lessonId ? {
      id: Number(lessonId) || 0,
      type: 'LESSON'
    } : {
      id: Number(contentId) || 0,
      type: 'TRACK'
    };
    if (!id || !language) {
      return 0;
    }
    const sessionKey = JSON.stringify({
      id,
      type,
      language: language.toUpperCase()
    });
    const savedTime = sessionStorage.getItem(sessionKey);
    const parsed = Number(savedTime);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [startTime, lessonId, shortId, trackId, contentId, language]);
  const effectiveStartTime = getSavedStartTime();
  return videoId ? /*#__PURE__*/_jsx("div", {
    "data-test-id": "video-player-container",
    children: /*#__PURE__*/_jsx(ACVideoPlayer, {
      autoPlay: autoPlay,
      creatorHubId: creatorHubId,
      creatorRegion: creatorRegion || na1,
      mutedByDefault: mutedByDefault,
      onActivation: handleActivation,
      onError: hsVideoOnError,
      onFinish: handleFinish,
      onLoaded: onVideoLoaded,
      onPlay: handlePlay,
      onPlayBackRateChange: hanldeOnPlayBackRateChange,
      onProgress: handleOnProgress,
      onReady: handleReady,
      onTimeProgress: handleMinutesWatched,
      onTimeUpdate: onTimeUpdate,
      playbackRate: playbackRate,
      startTime: effectiveStartTime,
      subtitleLanguage: captionLanguage,
      videoId: videoId
    }, contentId)
  }) : /*#__PURE__*/_jsx(Image, {
    alt: I18n.text('learningCenter.overviewModal.thumbnail'),
    "data-test-id": "fallback-thumbnail",
    src: fallbackThumbnailUrl || ''
  });
};
const EmbeddedVideoTrackLesson = _ref => {
  let {
      contentId,
      lessonId,
      language,
      trackId
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const videoProps = useVideo({
    contentId,
    lessonId,
    trackId,
    language
  });
  if (!videoProps) return null;
  return /*#__PURE__*/_jsx(EmbeddedVideoBase, Object.assign({
    contentId: contentId,
    lessonId: lessonId,
    trackId: trackId
  }, videoProps, props, {
    language: language
  }));
};
const EmbeddedVideoShort = _ref2 => {
  let {
      shortId
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const videoProps = Object.assign({}, useShortVideo({
    shortId: shortId
  }));
  if (!videoProps) return null;
  return /*#__PURE__*/_jsx(EmbeddedVideoBase, Object.assign({
    contentId: shortId.toString(),
    shortId: shortId
  }, videoProps, props));
};
export const EmbeddedVideo = _ref3 => {
  let {
      shortId
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  if (shortId) return /*#__PURE__*/_jsx(EmbeddedVideoShort, Object.assign({
    shortId: shortId
  }, props));else return /*#__PURE__*/_jsx(EmbeddedVideoTrackLesson, Object.assign({}, props));
};
export default EmbeddedVideo;